import { useMemo } from 'react'

import { useTagsQueries } from 'hooks-queries/tags'
import type { TTagStatus } from 'modules/tags/common/tags.types'

import { GET_TAGS_MONITOR_LOG } from './use-tags-queries-list.consts'
import type {
  TUseTagsQueriesListData,
  TUseTagsQueriesListReturn,
  ITerritoryTagFromApi,
  IUseTagsQueriesListProps,
} from './use-tags-queries-list.types'

export const useTagsQueriesList = (props: IUseTagsQueriesListProps): TUseTagsQueriesListReturn => {
  const { data, called, error, loading, refetch } = useTagsQueries({
    errorLog: GET_TAGS_MONITOR_LOG,
    queryName: 'listTerritoryTagsPaginated',
    options: {
      notifyOnNetworkStatusChange: true,
    },
    queryVariables: {
      pagination: { take: 30 },
      providers: props.providers,
    },
  })

  const tagsListData = useMemo((): TUseTagsQueriesListData => {
    if (!data?.listTerritoryTagsPaginated?.data) return []

    return (data.listTerritoryTagsPaginated.data as ITerritoryTagFromApi[]).map(tag => ({
      ...tag,
      territoryId: tag.territoryId,
      name: tag.name,
      status: (tag.status || 'processing') as TTagStatus,
      expiresAt: tag.expiresAt || '',
      daysRemaining: tag.daysRemaining || 0,
      isFollowed: tag.isFollowed || false,
      metrics: {
        views: tag.territoryPerformance?.sampledMetrics?.avgViews || 0,
        likes: tag.territoryPerformance?.sampledMetrics?.avgEngagement || 0,
        videos: tag.territoryPerformance?.totalPosts || 0,
      },
    }))
  }, [data])

  const totalCount = data?.listTerritoryTagsPaginated?.totalCount || 0

  const activeTags = useMemo(
    () =>
      tagsListData
        ?.filter(tag => tag.status === 'active')
        .map(tag => ({
          territoryId: tag.territoryId,
          name: tag.name,
        })) || [],
    [tagsListData]
  )

  return {
    tagsListData,
    tagsListLoading: loading,
    tagsListCalled: called,
    tagsListError: error,
    tagsListRefetch: refetch,
    activeTags,
    totalCount,
  }
}
